import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, HelpCircle, ArrowLeft, Loader2, MapPin, AlertTriangle } from "lucide-react";
import { SorinHeader } from "@/components/SorinHeader";
import { motion } from "framer-motion";

const Corroborate = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [report, setReport] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [workerNames, setWorkerNames] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, [reportId]);

  const loadData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/signup');
      return;
    }

    const { data: profileData } = await supabase
      .from('worker_profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (!profileData) {
      navigate('/signup');
      return;
    }

    setProfile(profileData);

    // Fetch worker names for redaction
    const { data: workers } = await supabase
      .from('worker_profiles')
      .select('display_name, full_name')
      .eq('company_id', profileData.company_id);

    if (workers) {
      const names: string[] = [];
      workers.forEach((w: any) => {
        if (w.display_name) names.push(w.display_name);
        if (w.full_name) names.push(w.full_name);
      });
      setWorkerNames([...new Set(names.filter(n => n && n.length >= 2))]);
    }

    const { data: existingCorroboration } = await supabase
      .from('peer_corroborations')
      .select('*')
      .eq('report_id', reportId)
      .eq('corroborator_id', profileData.id)
      .maybeSingle();

    if (existingCorroboration) {
      setIsSubmitted(true);
    }

    const { data: reportData, error } = await supabase
      .from('peer_reports')
      .select(`
        *, 
        reporter:worker_profiles!reporter_id(id, company_id),
        reported_worker:worker_profiles!reported_worker_id(display_name, full_name)
      `)
      .eq('id', reportId)
      .single();

    if (error || !reportData) {
      toast({ title: "Error", description: "Laporan tidak ditemukan", variant: "destructive" });
      navigate('/home');
      return;
    }

    setReport(reportData);
  };

  const handleCorroboration = async (status: 'confirmed' | 'denied' | 'no_info') => {
    if (!profile || !reportId) return;

    setIsLoading(true);
    try {
      const { error } = await supabase.from('peer_corroborations').insert({
        report_id: reportId,
        corroborator_id: profile.id,
        corroboration_status: status,
        notes: notes || null
      });

      if (error) throw error;

      // Create corroboration network connection if confirmed
      if (status === 'confirmed' && report?.reporter_id) {
        const weight = 0.8;
        await supabase.from('worker_network_connections').upsert([
          {
            worker_a: profile.id,
            worker_b: report.reporter_id,
            connection_type: 'corroboration',
            connection_weight: weight,
            company_id: profile.company_id,
            last_interaction: new Date().toISOString()
          },
          {
            worker_a: report.reporter_id,
            worker_b: profile.id,
            connection_type: 'corroboration',
            connection_weight: weight,
            company_id: profile.company_id,
            last_interaction: new Date().toISOString()
          }
        ], { onConflict: 'worker_a,worker_b,connection_type' });
      }

      // Trigger recalculation of evidence score to update status
      try {
        await supabase.functions.invoke('calculate-evidence-score', {
          body: { reportId }
        });
      } catch (recalcError) {
        console.error('Failed to recalculate evidence score:', recalcError);
      }

      setIsSubmitted(true);
      toast({ title: "Terima kasih!", description: "Konfirmasi kamu sudah tersimpan" });
      setTimeout(() => navigate('/home'), 2000);
    } catch (error) {
      console.error('Corroboration error:', error);
      toast({ title: "Gagal menyimpan", description: "Silakan coba lagi", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (!report || !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-warning/10 rounded-full blur-3xl" />
      </div>

      <SorinHeader />
      
      <main className="container max-w-2xl mx-auto px-4 py-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 mb-6"
        >
          <Button variant="outline" size="icon" onClick={() => navigate('/home')} className="rounded-xl">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Konfirmasi Laporan</h1>
            <p className="text-sm text-muted-foreground">Apakah kamu melihat bahaya ini?</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-3xl p-6 border border-border/50"
        >
          {isSubmitted ? (
            <div className="text-center py-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="h-20 w-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="h-10 w-10 text-success" />
              </motion.div>
              <h3 className="text-xl font-bold mb-2">Sudah Dikonfirmasi</h3>
              <p className="text-muted-foreground">Terima kasih atas bantuan kamu!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Natural conversational question - use description directly */}
              <div className="p-4 rounded-xl bg-muted/50 border">
                <p className="text-base text-foreground leading-relaxed">
                  Hari ini saat bekerja di <span className="font-medium">{report.location}</span>, apakah kamu melihat{' '}
                  <span className="font-semibold text-primary">{report.description}</span>
                  {report.work_activity && ` saat ${report.work_activity}`}?
                </p>
              </div>

              {report.photo_url && (
                <img 
                  src={report.photo_url} 
                  alt="Hazard" 
                  className="rounded-xl w-full max-w-md"
                />
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-medium mb-2 block">Catatan (Opsional)</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Tambahkan informasi tambahan jika ada..."
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={() => handleCorroboration('confirmed')}
                  disabled={isLoading}
                  className="w-full h-12 rounded-xl bg-success hover:bg-success/90"
                  size="lg"
                >
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Ya, Saya Lihat
                </Button>
                
                <Button
                  onClick={() => handleCorroboration('denied')}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full h-12 rounded-xl"
                  size="lg"
                >
                  <XCircle className="mr-2 h-5 w-5" />
                  Tidak, Saya Tidak Lihat
                </Button>
                
                <Button
                  onClick={() => handleCorroboration('no_info')}
                  disabled={isLoading}
                  variant="ghost"
                  className="w-full h-12 rounded-xl"
                  size="lg"
                >
                  <HelpCircle className="mr-2 h-5 w-5" />
                  Tidak Yakin / Tidak Ada Info
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default Corroborate;